import React, { createContext, useContext, useEffect, useState } from "react";

export type Theme = "dark" | "light";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isControlled: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: React.ReactNode;
    forcedTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, forcedTheme }) => {
  const [internalTheme, setInternalTheme] = useState<Theme>("light");

  const isControlled = forcedTheme !== undefined;
  const theme = forcedTheme || internalTheme;

  useEffect(() => {
    // Only access localStorage/system pref if not controlled
    if (!isControlled) {
        const stored = localStorage.getItem("theme") as Theme;
        if (stored) {
            setInternalTheme(stored);
        } else if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
            setInternalTheme("dark");
        }
    }
  }, [isControlled]);

  useEffect(() => {
    // Only manipulate root document if not controlled
    if (!isControlled) {
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(theme);
        localStorage.setItem("theme", theme);
    }
  }, [theme, isControlled]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setInternalTheme, isControlled }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};